<script lang="ts">
  import Icon from "../components/Icon.svelte"
  import carExchange from "../Icons/carExchange.svg?raw"
  import Close from "../../component_library/components/svelte/Close.svelte"
  import { getContext, onMount, createEventDispatcher } from "svelte"
  import Button from "../../component_library/components/svelte/Button.svelte"
  import InputLpn from "../../component_library/components/svelte/InputLpn.svelte"
  import ModuleTypes from "../constants/ModuleTypes"
  import IntegrationTypes from "../constants/IntegrationTypes"
  import { buildUrlData } from "../lib/buildModuleUrlData"
  import { hexToRgba } from "../../lib/helpers"
  import type { Theme } from "website_script/types/theme"
  import type { Texts } from "website_script/types/texts"
  import type { Settings } from "website_script/types/settings"
  import type { Environment } from "website_script/types/environment"

  /** Context */
  const {
    accentColor,
    textCtaColor,
    legacyPromptHeadingColor,
    legacyPromptIconColor,
    sharpCorners,
  } = getContext<Theme>("theme")
  const { licensePlateCountry, iframeHost, auCarUrl } =
    getContext<Settings>("settings")
  let accentColor10 = hexToRgba(accentColor, 0.1)
  let accentColor20 = hexToRgba(accentColor, 0.2)
  let boxShadowColor8 = hexToRgba(accentColor, 0.08)
  let boxShadowColor16 = hexToRgba(accentColor, 0.16)

  const settings = getContext<Settings>("settings")
  const { enableLicensePlateInput, showHyundaiExtraConsentText } = settings
  const { abTesting, trackFunction } = getContext<Environment>("environment")
  const { legacyPromptTitle, legacyPromptButtonText } =
    getContext<Texts>("texts")
  const texts = getContext<Texts>("texts")

  /** Trigger for the root modal */
  export let onModalTrigger

  /** Set an empty value for lpn input */
  let lpnNumber = ""
  let placeholder = texts.lpnInputPlaceholderText

  /** Set mobile size */
  let mobile = window.screen.width < 601

  /**
   * Stores currently typed license plate number
   * Opens the modal on enter with given license plate number
   */
  const lpnKeydownHandler = (e: KeyboardEvent) => {
    if (e.keyCode === 13) {
      clickHandler()
    }
  }

  const dispatch = createEventDispatcher()

  /**
   * Closes the Legacy Prompt via dispatching the close event
   */
  const closeLegacyPrompt = (e: MouseEvent) => {
    // Stop the click event propagation so it doesn't fire other things
    e.stopPropagation()
    dispatch("close")
  }

  const trackButtonClick = () => {
    trackFunction({
      entity: "engagement-widget",
      element: "prompt legacy: trade_in",
      action: "clicked",
      type: "interacted_with_cta",
      flow: "trade_in",
    })
  }

  /**
   * When clicking a button, track the interaction and open the modal
   */
  const clickHandler = (event?: MouseEvent) => {
    // Don't open the modal when clicking on the input element
    if (
      event &&
      (event.target as HTMLElement).classList.contains("auws-lpn-input")
    ) {
      return
    }

    trackButtonClick()
    let urlData = buildUrlData({
      settings,
      abTesting: $abTesting,
      moduleType: ModuleTypes.TRADE_IN,
      integrationType: IntegrationTypes.LEGACY_PROMPT,
    })
    urlData = lpnNumber ? { ...urlData, vin_lpn: lpnNumber } : urlData
    onModalTrigger(urlData)
  }

  onMount(() => {
    trackFunction({
      entity: "engagement-widget",
      element: "prompt legacy: trade_in",
      action: "displayed",
      type: "displayed_cta",
      flow: "trade_in",
    })
  })
</script>

<!--
@component
Shows a very old version of our DCTA, migrated from CoffeeScript.
It is being shown out of the Engagement widget, using the single message feature/LPN version
-->

<div
  class="auws-container"
  data-testid="legacy-dcta-prompt"
  style={`
  border-radius: ${sharpCorners ? "0px" : "5px"};
  --accent-color: ${accentColor};
  --accent-color-10: ${accentColor10};
  --accent-color-20: ${accentColor20};
  --box-shadow-color-8: ${boxShadowColor8};
  --box-shadow-color-16: ${boxShadowColor16}`}
>
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div
    class="auws-wrapper"
    data-testid="legacy-auws-wrapper"
    on:click={clickHandler}
    style={`border-radius: ${sharpCorners ? "0px" : "5px"}`}
  >
    <div class="auws-close">
      <Close on:click={closeLegacyPrompt} />
    </div>
    <div class="auws-header">
      {#if !mobile}
        <Icon
          size="100px"
          svgProps={{
            data: carExchange,
            alt: legacyPromptTitle,
            stroke: legacyPromptIconColor,
            fill: "none",
          }}
        />
      {/if}
      <div
        class={`auws-heading
          ${mobile && !enableLicensePlateInput ? "auws-heading-mobile" : ""}`}
        style={`color: ${legacyPromptHeadingColor}`}
      >
        {@html legacyPromptTitle}
      </div>
    </div>
    {#if enableLicensePlateInput}
      <div class="auws-hidden">
        {#if showHyundaiExtraConsentText}
          <p class="auws-consent">
            {texts.hyundaiExtraConsentText}
          </p>
        {/if}
        <p class="auws-legal">
          {texts.lpnDisclaimerText}
        </p>
      </div>
    {/if}
    <div class="auws-wrapper-lpn">
      {#if enableLicensePlateInput && licensePlateCountry}
        <InputLpn
          {placeholder}
          {licensePlateCountry}
          on:keydown={lpnKeydownHandler}
          bind:lpnNumber
        />
      {/if}
      <Button
        extraClasses="auws-lpn-submit"
        style={`background-color: ${accentColor}; color: ${textCtaColor}`}
        {clickHandler}
      >
        {@html legacyPromptButtonText}</Button
      >
    </div>

    <div class="auws-footer">
      {texts.footerSignature}
      <a href={auCarUrl ?? iframeHost} target="_blank" class="auws-footer-link"
        ><img
          class="auws-footer-logo"
          src="https://assets.autouncle.com/assets/au_logo_tiny-1c6221c7af5c8e787f8445868e25c1a82c04798a5c6a23894a1f122f74691fd7.png"
          alt="AutoUncle"
        />
      </a>
    </div>
  </div>
</div>

<style>
  .auws-container .auws-wrapper {
    position: relative;
    box-sizing: border-box;
    z-index: 2147483646; /* max value - 1 (less than modal) */
    bottom: 0;
    height: auto;
    background: rgba(247, 247, 247, 0.96);
    border: 1px solid var(--accent-color-20);
    box-shadow:
      0 1px 1px 0 var(--box-shadow-color-8),
      0 1px 3px 1px var(--box-shadow-color-16);
    transition-duration: 0.35s;
    padding: 0 24px;
    width: 330px;
    cursor: pointer;
  }

  .auws-container .auws-wrapper:hover {
    box-shadow:
      0 1px 3px 1px var(--accent-color-20),
      0 2px 8px 4px var(--accent-color-10);
  }

  .auws-close {
    opacity: 0;
  }
  .auws-container:hover .auws-close {
    opacity: 1;
  }

  .auws-heading {
    text-align: center;
    width: 100%;
    margin-top: 18px;
    margin-bottom: 0px;
    font-size: 18px;
    font-weight: bold;
  }

  .auws-hidden {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition:
      max-height 0.55s,
      opacity 0.35s;
  }

  .auws-legal {
    font-size: 10px;
    color: #000;
    text-align: left;
    line-height: 150%;
    margin-bottom: 10px;
  }

  .auws-consent {
    font-size: 10px;
    color: #000;
    text-align: left;
    line-height: 150%;
    margin-bottom: 10px;
  }

  .auws-wrapper-lpn {
    width: 100%;
  }

  .auws-wrapper :global(.auws-lpn-submit) {
    display: block;
    max-width: 100%;
    box-sizing: border-box;
    padding: 10px;
    text-align: center;
    background: var(--accent-color);
    color: #ffffff;
    font-size: 18px;
    line-height: 1.42857;
    border-radius: 5px;
    cursor: pointer;
    transition-duration: 0.15s;
    box-shadow:
      0 1px 1px 0 var(--box-shadow-color-8),
      0 1px 3px 1px var(--box-shadow-color-16);
  }

  .auws-wrapper :global(.auws-lpn-submit):hover {
    box-shadow:
      0 1px 3px 1px var(--accent-color-20),
      0 2px 8px 4px var(--accent-color-10);
  }

  .auws-footer {
    font-size: 8px;
    text-align: right;
    padding: 6px 0;
    color: #000;
  }

  .auws-footer-logo {
    height: 6px;
    width: auto;
    min-width: auto;
    display: inline;
  }

  .auws-footer-link {
    display: inline;
    text-decoration: none;
    padding: 0;
    margin: 0;
  }

  .auws-wrapper :global(.auws-lpn-submit:hover) {
    box-shadow: 0px 0px 3px var(--accent-color);
  }

  @media screen and (max-width: 601px) {
    .auws-container .auws-wrapper {
      width: 100vw;
      /*
       * The engagement widget has margins, but we want
       * the legacy prompt to fill the screen from left to right
       */
      margin-left: calc(0px - var(--side-edge-spacing));
      margin-right: calc(0px - var(--side-edge-spacing));
      /* We want the legacy prompt to touch the bottom of the screen */
      margin-bottom: calc(0px - var(--bottom-edge-spacing));
      background: rgba(247, 247, 247, 0.96);
    }
    .auws-wrapper:hover {
      box-shadow: none;
    }
    .auws-close {
      visibility: visible;
      opacity: 1;
    }

    .auws-header {
      display: flex;
      align-items: center;
    }

    .auws-heading-mobile {
      margin-bottom: 18px;
    }

    .auws-hidden {
      max-height: 100px;
      opacity: 1;
    }
    .auws-wrapper :global(.auws-icon-svg) {
      display: none;
    }
  }

  @media screen and (min-width: 601px) {
    .auws-container:hover .auws-hidden {
      max-height: 400px;
      opacity: 1;
    }

    .auws-container:hover .auws-close {
      visibility: visible;
    }
    .auws-container .auws-header {
      display: flex;
      min-height: 80px;
      align-items: center;
    }
  }
</style>
